import { useRef, useState, useEffect } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Buffer } from 'buffer';

import { useCompanyValidationStore } from 'context/CompanyValidationProvider/hooks';
import { SET_STEP, STEP_ACCEPTED, STEP_ERROR_LOADING, STEP_REJECTED } from 'context/CompanyValidationProvider/constants';
import { validateCompany } from 'services/validateCompany';
import BigIcon from 'UI/atoms/BigIcon';
import CardContent from 'UI/atoms/CardContent';
import Subtitle from 'UI/atoms/Subtitle';
import TextBox from 'UI/atoms/TextBox';
import Title from 'UI/atoms/Title';
import Loader from 'UI/atoms/Loader';
import Notice from 'UI/atoms/Notice';
import ReCaptcha from 'UI/atoms/ReCaptcha';
import Form from 'UI/molecules/Form';

import building from 'assets/img/icon/building.svg';

import styles from './styles.module.scss';

const CompanyValidation = () => {
  const { t } = useTranslation();
  const { code, token } = useParams();
  const [processingAcceptation, setProcessingAcceptation] = useState(false);
  const [processingRejection, setProcessingRejection] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  const [error, setError] = useState(null);
  const recaptchaRef = useRef();
  const [, registerDispatch] = useCompanyValidationStore();
  const [validRecaptcha, setValidRecaptcha] = useState(false);

  useEffect(() => {
    window.sessionStorage.setItem('token', token);
    try {
      setCompanyData(JSON.parse(Buffer.from(code, 'base64').toString('utf8')));
    } catch (e) {
      registerDispatch({ type: SET_STEP, payload: STEP_ERROR_LOADING });
    }
  }, []);

  const submit = async (accepted) => {
    accepted ? setProcessingAcceptation(true) : setProcessingRejection(true);
    setError(null);
    const recaptcha = recaptchaRef.current.getValue();
    const data = {
      accept_assignation: accepted,
      recaptcha,
      validation_token: token,
      lead_id: companyData.leadID
    };
    validateCompany(data)
      .then((result) => {
        if (!result.success) {
          setError({ message: t('api.error') });
          return;
        }
        if (!accepted) {
          registerDispatch({ type: SET_STEP, payload: STEP_REJECTED });
          return;
        }
        registerDispatch({ type: SET_STEP, payload: STEP_ACCEPTED });
      })
      .catch((e) => {
        setError({ message: t('api.error') });
      })
      .finally(() => {
        setProcessingAcceptation(false);
        setProcessingRejection(false);
      });
  };

  const onChangeRecaptcha = () => {
    const recaptchaValue = recaptchaRef.current.getValue();
    if (recaptchaValue) {
      setValidRecaptcha(true);
    }
  };

  const onExpiredRecaptcha = () => {
    setValidRecaptcha(false);
  };

  if (!companyData) {
    return <CardContent sm={{ span: 10, offset: 1 }}>
      <div>
        <Loader size={'big'} position={'relative'} color={'green'} />
        <Title>{t('company.validation.loading')}</Title>
      </div>
    </CardContent>;
  }

  return (
    <CardContent sm={{ span: 10, offset: 1 }}>
      <BigIcon src={building} />
      <Title>{t('company.validation.title')}</Title>
      <Row>
        <Col xs={12} sm={{ span: 10, offset: 1 }}>
          <Subtitle>
            {t('company.validation.subtitle')}
          </Subtitle>
        </Col>
      </Row>

      <div className={styles.data}>
        <TextBox color="blue">
          {companyData.companyName &&
            <p className={styles.name}>{companyData.companyName}</p>
          }
          {companyData.companyAddress &&
            <p className={styles.address}>{companyData.companyAddress}</p>
          }
        </TextBox>
      </div>

      {error && (
        <Notice color="red">
          <span dangerouslySetInnerHTML={{ __html: error.message }}></span>
        </Notice>
      )}

      <ReCaptcha
        style={{ marginTop: '0px', marginBottom: '15px' }}
        sitekey={process.env.REACT_APP_RECAPTCHA_KEY}
        onChange={onChangeRecaptcha}
        onExpired={onExpiredRecaptcha}
        ref={recaptchaRef} />

      <div className={styles.button}>
        <Form.Button disabled={!validRecaptcha || processingAcceptation || processingRejection}
          type="button" onClick={() => submit(true)}>
          {t('company.validation.button_accept')}
          {processingAcceptation && <Loader />}
        </Form.Button>
      </div>
      <div className={styles.button}>
        <Form.Button disabled={!validRecaptcha || processingAcceptation || processingRejection}
          type="button" style="white" onClick={() => submit(false)}>
          {t('company.validation.button_reject')}
          {processingRejection && <Loader color="green" />}
        </Form.Button>
      </div>
    </CardContent>
  );
};

export default CompanyValidation;
